import {
  createContext,
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";

import { ISortConfig } from "../../../../components/ui-kit/ReactTable/context/TableContext";
import { UserApiType } from "../../../../util/types";
import { useGetusers, usersFilters } from "../hooks/useGetUsers";
import { useUrlFilters } from "../../../../hooks/useUrlState";

const EMPTY_FILTERS: usersFilters = {};
interface IUsersContext {
  usersData: UserApiType[];
  refreshusers: (ownerId?: string) => void;
  setusersFilters: (filters: Record<string, any>) => void;
  onPaginationChange: (pageValue: number, limitValue: number) => void;
  onSortChange: (val?: ISortConfig) => void;
  loading: boolean;
  totalDocs: number;
  page: number;
  limit: number;
  error: string | null;
  filters: usersFilters;
  setVerfificationStatus: (status: string | undefined) => void;
  setOwner: (companyId: string | undefined) => void;
}

const UsersContext = createContext<IUsersContext>({
  usersData: [],
  refreshusers: () => null,
  loading: false,
  setusersFilters: () => null,
  onPaginationChange: () => null,
  onSortChange: () => null,
  totalDocs: 0,
  page: 1,
  limit: 25,
  error: null,
  filters: {},
  setVerfificationStatus: () => null,
  setOwner: () => null,
});

export const UsersContextProvider: FC<{
  children: ReactElement;
  /** Top-level Users page: filters, status + page live in the URL. */
  syncWithUrl?: boolean;
}> = ({ children, syncWithUrl = false }) => {
  // `status` is an ordinary filter here (the Users page has no status tabs),
  // so it is not reserved and round-trips through the URL with the rest.
  const [filters, setFilters] = useUrlFilters<usersFilters>(EMPTY_FILTERS, {
    enabled: syncWithUrl,
  });
  const [userStatus, setStatus] = useState<string | undefined>();
  const [companyId, setCompanyId] = useState<string | undefined>();
  const [sort, setSort] = useState<ISortConfig>({
    field: "createdAt",
    direction: "DESC",
  });
  const {
    users,
    refreshusers,
    onPaginationChange,
    loading,
    totalDocs,
    page,
    limit,
    error,
  } = useGetusers(filters, sort, userStatus, companyId, { syncWithUrl });
  const [usersData, setUsersData] = useState<UserApiType[]>([]);

  useEffect(() => {
    setUsersData(users);
  }, [users]);

  return (
    <UsersContext.Provider
      value={{
        usersData,
        refreshusers,
        loading,
        totalDocs,
        page,
        limit,
        onPaginationChange,
        setusersFilters: setFilters,
        onSortChange: setSort,
        error,
        filters,
        setVerfificationStatus: setStatus,
        setOwner: setCompanyId,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};

export const useUsersContext = () => {
  return useContext(UsersContext);
};
